import {
  type ComponentProps,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import intl from '@frameio/i18n/intl';
import { EmptyList } from '@frameio/notifications/components/NotificationsList/components/EmptyList';
import { useTrack } from '@frameio/shared/segment/hooks';
import {
  DEFAULT_SUPPORTED_LOCALE,
  toSupportedLocaleOrDefault,
} from '@frameio/shared/utils/toSupportedLocale';
import { getFormattedDate } from '@frameio/utils';
import {
  AlertInline,
  Box,
  List,
  ListItem,
  SheetHead,
  Text,
} from '@frameio/vapor';
import { theme } from '@frameio/vapor-theme';
import { PAGE_SIZE, SHOW_ALL_ITEMS_PARAM } from './constants';
import TEXT from './generated/TEXT/WhatsNewSheet';
import { useWhatsNewItemDataFragment } from './gql/generated/WhatsNewItem';
import { useGetWhatsNewFeedQuery } from './gql/queries/generated/GetWhatsNewFeed';
import { useActiveWhatsNewItem } from './hooks/useActiveWhatsNewItem';
import type { ListItem as WhatsNewListItem } from './WhatsNewContext';
import { useWhatsNew } from './WhatsNewContext';

TEXT.DEFINE({
  /** Sheet title shown at the top of the "What's New" panel */
  title: 'What’s New',
  /** Empty state title shown when there are no "What's New" items to display */
  emptyListTitle: 'No news available',
  /** Empty state call-to-action label for viewing all items */
  emptyListCta: 'View All',
  /** Link text for opening the full "What's New" item in a new tab */
  learnMore: 'Learn more',
  /** Banner text for when translations are not yet available */
  translationUnavailable:
    'Articles are currently available in English only. Additional languages will be available soon.',
});

type ListItemContentProps = {
  id: string;
};

const StyledContainerDiv = styled.div`
  align-items: stretch;
  cursor: pointer;
  display: flex;
  flex: 1;
  flex-direction: column;
  isolation: isolate;
  justify-content: center;
  min-width: 0;
`;

const StyledThumbnailImg = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const StyledPlaceholder = styled.div`
  align-items: center;
  background: radial-gradient(
      circle at 20% 240%,
      transparent,
      ${theme.colors.meta.lowOpacity.blue} 50%,
      ${theme.colors.meta.mediumOpacity.blue} 75%,
      ${theme.colors.meta.lowOpacity.blue} 75%
    ),
    radial-gradient(
      circle at 20% 240%,
      transparent,
      ${theme.colors.meta.lowOpacity.purple} 50%,
      ${theme.colors.meta.mediumOpacity.purple} 75%,
      ${theme.colors.meta.lowOpacity.purple} 75%
    ),
    radial-gradient(
      circle at 150%,
      ${theme.colors.meta.mediumOpacity.orange},
      transparent 75%
    ),
    linear-gradient(
      230deg,
      ${theme.colors.meta.mediumOpacity.purple},
      transparent 70%
    ),
    ${theme.colors.background.base};
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const StyledReadMoreLink = styled(Link)`
  cursor: pointer;
  display: inline-flex;
  margin-left: ${theme.spaces[4]};
`;

const StyledBannerContainer = styled.div`
  padding: ${theme.spaces[16]} ${theme.spaces[24]} 0;
`;

const truncateAtWord = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;

  const truncated = text.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  // If we found a space, truncate at that space
  if (lastSpaceIndex > 0) {
    return `${truncated.slice(0, lastSpaceIndex)}...`;
  }

  // If no space found, just truncate at maxLength
  return `${truncated}...`;
};

// Component for rendering a single item in the list
const WhatsNewListItem = ({ id }: ListItemContentProps) => {
  const { data: item } = useWhatsNewItemDataFragment({
    from: { __typename: 'WhatsNewItem', id },
  });

  if (!item) {
    return null;
  }

  const renderDate = () => {
    if (!item.date) {
      return null;
    }

    const { formattedDate } = getFormattedDate({
      date: new Date(item.date),
      returnBothDateAndTime: false,
    });

    return (
      <div>
        <Text
          as="p"
          color="tertiary"
          display="inline"
          fontWeight="medium"
          truncate={1}
          variant="caption"
        >
          {formattedDate}
        </Text>
      </div>
    );
  };

  return (
    <StyledContainerDiv>
      <Box
        borderRadius={4}
        display="flex"
        overflow="hidden"
        position="relative"
        style={{
          marginBottom: theme.spaces[16],
          height: '158px',
          backgroundColor: theme.colors.background.secondary,
        }}
      >
        {item.imageUrl && item.title ? (
          <StyledThumbnailImg
            alt={item.title}
            loading="lazy"
            src={item.imageUrl}
          />
        ) : (
          <StyledPlaceholder />
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={8}>
        <Box display="flex" flexDirection="column" gap={4}>
          <Text fontWeight="medium" truncate={1} variant="primary">
            {item.title}
          </Text>
          {item.description && (
            <Text
              as="p"
              color="tertiary"
              display="inline"
              variant="subtitleSmall"
            >
              {truncateAtWord(item.description, 95)}
              {item.link && (
                <StyledReadMoreLink href={item.link} target="_blank">
                  <Text color="link" variant="link">
                    {TEXT.learnMore}
                  </Text>
                </StyledReadMoreLink>
              )}
            </Text>
          )}
        </Box>
        {renderDate()}
      </Box>
    </StyledContainerDiv>
  );
};

// Main component for the WhatsNewSheet
export const WhatsNewSheet = memo(() => {
  const track = useTrack();

  const { setLastUpdatedTime, setLastViewedTime } = useWhatsNew();
  const locale = toSupportedLocaleOrDefault(intl().locale);

  const isNonDefaultLocale = locale !== DEFAULT_SUPPORTED_LOCALE;

  const { data, loading, error } = useGetWhatsNewFeedQuery({
    variables: {
      page: {
        first: PAGE_SIZE,
      },
      locale,
    },
  });

  const localeHasItems = (data?.whatsNewFeed?.items?.nodes?.length ?? 0) > 0;
  const shouldUseFallback = isNonDefaultLocale && !loading && !localeHasItems;

  const {
    data: fallbackData,
    loading: fallbackLoading,
    error: fallbackError,
  } = useGetWhatsNewFeedQuery({
    variables: {
      page: {
        first: PAGE_SIZE,
      },
      locale: DEFAULT_SUPPORTED_LOCALE,
    },
    skip: !shouldUseFallback,
  });

  const feedData = shouldUseFallback ? fallbackData : data;
  const feedLoading = loading || (shouldUseFallback && fallbackLoading);
  const feedError = shouldUseFallback ? fallbackError : error;

  const items = useMemo(() => {
    return (
      (feedData?.whatsNewFeed?.items?.nodes.filter(
        (item) => item && item.id
      ) as WhatsNewListItem[]) || []
    );
  }, [feedData]);

  const onItemAction = useCallback(
    (itemId: string) => {
      track('whats-new-panel-item-clicked', {
        item_id: itemId,
      });
    },
    [track]
  );

  const { activeWhatsNewItemId } = useActiveWhatsNewItem();
  // scroll to the active item from param
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scrollToId, setScrollToId] = useState<string>();
  const scrollToIdTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!feedLoading && feedData) {
      scrollToIdTimeout.current = setTimeout(() => {
        if (
          activeWhatsNewItemId &&
          activeWhatsNewItemId !== SHOW_ALL_ITEMS_PARAM
        ) {
          setScrollToId(activeWhatsNewItemId);
        }
      }, 200);
    }
    return () => {
      if (scrollToIdTimeout.current) {
        clearTimeout(scrollToIdTimeout.current);
      }
    };
  }, [feedLoading, feedData, activeWhatsNewItemId, setScrollToId]);

  const onScrollComplete = useCallback(() => {
    if (activeWhatsNewItemId) {
      setSelected(new Set<string>([activeWhatsNewItemId]));
    }
  }, [activeWhatsNewItemId]);

  useEffect(() => {
    // if data is available, set the last updated time and set the last viewed timestamp
    if (feedData) {
      // Set the last updated time from the gql query
      const lastUpdated = feedData.whatsNewFeed?.lastUpdated || 0;
      setLastUpdatedTime(new Date(lastUpdated).getTime());
      const currentTime = new Date();
      // Set the last viewed since we opened the sheet
      setLastViewedTime(currentTime.getTime());
    }
  }, [feedData, setLastUpdatedTime, setLastViewedTime]);

  let loadingState: ComponentProps<typeof List>['loading'];

  if (feedLoading) {
    loadingState = 'loading';
  } else if (feedError) {
    loadingState = 'loading-error';
  }
  return (
    <>
      <SheetHead title={TEXT.title} />
      {isNonDefaultLocale && (
        <StyledBannerContainer>
          <Text>
            <AlertInline>{TEXT.translationUnavailable}</AlertInline>
          </Text>
        </StyledBannerContainer>
      )}
      <List
        animate={true}
        estimatedItemHeight={280}
        itemGap={8}
        items={items}
        loading={loadingState}
        onScrollComplete={onScrollComplete}
        paddingBottom={16}
        paddingTop={16}
        renderPlaceholder={() => {
          const text = {
            emptyListTitle: TEXT.emptyListTitle,
            emptyListCta: TEXT.emptyListCta,
          };

          return <EmptyList shouldRender={true} text={text} />;
        }}
        scrollPadding={{ top: 12, bottom: 12 }}
        scrollToKey={scrollToId}
        selectedKeys={selected}
        selectionMode="single"
        variant="list"
      >
        {(item: WhatsNewListItem) => (
          <ListItem
            key={item.id}
            href={item.link}
            onAction={() => onItemAction(item.id)}
            target="_blank"
            textValue={item.title}
          >
            <WhatsNewListItem id={item.id} />
          </ListItem>
        )}
      </List>
    </>
  );
});
